import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, DollarSign, Percent, Coins, Calendar, CalendarDays } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface InvestmentMetrics {
  totalDeposits: number;
  totalWithdrawals: number;
  netContribution: number;
  returnOnInvestment: number | null;
  totalInterest: number;
  currentMonthInterest: number;
  currentYearInterest: number;
}

interface InvestmentMetricsCardsProps {
  metrics: InvestmentMetrics | undefined;
  currency: string;
  isLoading: boolean;
}

export function InvestmentMetricsCards({ metrics, currency, isLoading }: InvestmentMetricsCardsProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (value: number | null) => {
    if (value === null) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const cards = [
    {
      title: 'Total Deposits',
      value: metrics ? formatCurrency(metrics.totalDeposits) : formatCurrency(0),
      icon: TrendingUp,
      color: 'text-green-600',
    },
    {
      title: 'Total Withdrawals',
      value: metrics ? formatCurrency(metrics.totalWithdrawals) : formatCurrency(0),
      icon: TrendingDown,
      color: 'text-red-600',
    },
    {
      title: 'Net Contribution',
      value: metrics ? formatCurrency(metrics.netContribution) : formatCurrency(0),
      icon: DollarSign,
      color: metrics && metrics.netContribution >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Return on Investment',
      value: metrics ? formatPercentage(metrics.returnOnInvestment) : 'N/A',
      icon: Percent,
      color: metrics && metrics.returnOnInvestment !== null && metrics.returnOnInvestment >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Total Interest',
      value: metrics ? formatCurrency(metrics.totalInterest) : formatCurrency(0),
      icon: Coins,
      color: 'text-blue-600',
    },
    {
      title: 'Current Month Interest',
      value: metrics ? formatCurrency(metrics.currentMonthInterest) : formatCurrency(0),
      icon: Calendar,
      color: 'text-purple-600',
    },
    {
      title: 'Current Year Interest',
      value: metrics ? formatCurrency(metrics.currentYearInterest) : formatCurrency(0),
      icon: CalendarDays,
      color: 'text-indigo-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" role="region" aria-label="Investment metrics summary">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className={`h-4 w-4 ${card.color}`} aria-hidden="true" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" aria-label={`${card.title}: ${card.value}`}>
              {card.value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
